import { useMemo } from 'react';

import {
  ChartTimeSeriesLine,
  ChartTimeSeriesLineProps,
  ChartTimeSeriesLineTheme,
  LINE_COLOR,
} from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';
import { calcMobileMarginAdjustment, getD3NumberFormat } from '@hcs/utils';

import { CerberusStatsRpiTS } from '../../types';

const FORECAST_RPI_DATA_ID = 'forecastRpi';
const LEGEND_ITEMS: ChartTimeSeriesLineProps['legendItems'] = [
  {
    id: 'historical',
    title: 'Historical RPI',
    background: { type: 'single', color: LINE_COLOR },
  },
  {
    id: 'forecast',
    title: 'Forecasted RPI',
    background: { type: 'single', color: LINE_COLOR },
    isDotDashed: true,
  },
];

const D3_Y_FORMAT = getD3NumberFormat({ precision: 1 });

export interface CerberusStatsRpiChartProps {
  dataHcName: string;
  className?: string;
  isLoading: boolean;
  data: CerberusStatsRpiTS | null | undefined;
  theme?: Partial<ChartTimeSeriesLineTheme>;
}
export const CerberusStatsRpiChart = ({
  className,
  dataHcName,
  isLoading,
  data,
  theme,
}: CerberusStatsRpiChartProps) => {
  const historicalTimeSeries = data?.historicalTimeSeries;
  const forecastTimeSeries = data?.forecastTimeSeries;
  const { isPhoneWidth } = useScreenSize();
  const chartData: ChartTimeSeriesLineProps['chartData'] = useMemo(() => {
    if (!historicalTimeSeries || !forecastTimeSeries) {
      return null;
    }
    const historicalLastRecord =
      historicalTimeSeries[historicalTimeSeries.length - 1];
    if (historicalLastRecord == null) {
      return null;
    }
    const forecastTimeSeriesWithLastHistoric = [
      historicalLastRecord,
      ...forecastTimeSeries,
    ];
    return [
      {
        id: 'historicalRpi',
        data:
          historicalTimeSeries?.map((record) => {
            return {
              x: `${record.month}`,
              y: record.valueIndexed,
            };
          }) || [],
      },
      {
        id: FORECAST_RPI_DATA_ID,
        data:
          forecastTimeSeriesWithLastHistoric?.map((record) => {
            return {
              x: `${record.month}`,
              y: record.valueIndexed,
            };
          }) || [],
      },
    ];
  }, [historicalTimeSeries, forecastTimeSeries]);

  const mergedTheme = {
    Container: className,
    ...theme,
  };

  const mobileMargin =
    isPhoneWidth && chartData?.[0].data
      ? calcMobileMarginAdjustment(chartData?.[0].data, D3_Y_FORMAT)
      : undefined;

  return (
    <ChartTimeSeriesLine
      dataHcName={dataHcName}
      isLoading={isLoading}
      chartData={chartData}
      title="Rental Price Index"
      tooltipDateFormat={'monthYear'}
      d3FormatterForY={D3_Y_FORMAT}
      theme={mergedTheme}
      forecastSerieId={FORECAST_RPI_DATA_ID}
      legendItems={LEGEND_ITEMS}
      tickRotationOverride={isPhoneWidth ? -60 : undefined}
      rightAxisMargin={mobileMargin}
      compactTitle={isPhoneWidth}
      compactValue={isPhoneWidth}
    />
  );
};
